<template>
  <div class="hierarchical-link-filter">
    <a
      v-if="selectedParent"
      class="hierarchical-link-filter__link ds-block ds-mb-4"
      :href="selectedParent.url"
      @click.prevent="emit('itemClicked', selectedParent)"
    >
      <FilterLabel
        :label="selectedParent.label"
        :selected="selectedParent.isRefined"
      >
        &#60;
      </FilterLabel>
    </a>
    <ul class="ds-flex ds-flex-col ds-gap-4">
      <li
        v-for="item in itemsToDisplay"
        :key="item.label"
      >
        <a
          class="hierarchical-link-filter__link"
          data-testid="categoryFilterLink"
          :href="item.url"
          @click.prevent="emit('itemClicked', item)"
        >
          <FilterLabel
            :label="item.label"
            :selected="item.isRefined"
          />
        </a>
        <ul
          v-if="item.isRefined && item.data?.length"
          class="ds-flex ds-flex-col ds-gap-4 ds-mt-4 ds-pl-4"
        >
          <li
            v-for="child in item.data"
            :key="child.label"
          >
            <a
              class="hierarchical-link-filter__link"
              data-testid="categoryFilterLink"
              :href="child.url"
              @click.prevent="emit('itemClicked', child)"
            >
              <FilterLabel
                :label="child.label"
                :selected="child.isRefined"
              />
            </a>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { Item, LinkItem } from './types';
import FilterLabel from './filter-label.vue';

const props = defineProps<{ items: Item[]; createUrl: (value: string) => string }>();
const emit = defineEmits(['itemClicked']);

const findSelectedL1 = (items: Item[]): Item | undefined => {
  return items.find((item) => item.isRefined);
};

const createLinkItem = (item: Item): LinkItem => {
  if (!item) {
    return undefined;
  }

  return {
    ...item,
    url: props.createUrl(item.value),
    data: item.data?.map((child) => createLinkItem(child)),
  };
};

const selectedParent = computed(() => createLinkItem(findSelectedL1(props.items)));
const itemsToDisplay = computed<LinkItem[]>(() => {
  if (selectedParent.value) {
    return selectedParent.value.data;
  }

  return props.items.map(createLinkItem);
});
</script>

<style scoped lang="scss">
.hierarchical-link-filter {
  &__link:hover :deep(.ds-text-primary) {
    @apply ds-text-neutral-800;
  }
}
</style>
